'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useAnimation } from '@/context/AnimationContext';

/**
 * WordByWordFade - Fades in text word by word on scroll
 * @param {string} text - Text to animate
 * @param {string} className - CSS classes for the container
 */
export default function WordByWordFade({ text, className = '' }) {
    const ref = useRef(null);
    const { isMobile, shouldAnimate } = useAnimation();
    const isInView = useInView(ref, {
        once: true,
        amount: 0.5
    });

    const words = text.split(' ');

    const containerVariants = {
        hidden: {},
        visible: {
            transition: {
                staggerChildren: 0.08
            }
        }
    };

    const wordVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.5
            }
        }
    };

    // Animation Logic:
    // Mobile: Render visible immediately (no animation).
    // Desktop: Wait for shouldAnimate (network idle) AND isInView.
    const isReadyToAnimate = isMobile || (shouldAnimate && isInView);

    return (
        <motion.span
            ref={ref}
            initial={isMobile ? "visible" : "hidden"}
            animate={isReadyToAnimate ? "visible" : "hidden"}
            variants={containerVariants}
            className={className}
        >
            {words.map((word, index) => (
                <motion.span
                    key={index}
                    variants={wordVariants}
                    style={{ display: 'inline-block', marginRight: '0.25em' }}
                >
                    {word}
                </motion.span>
            ))}
        </motion.span>
    );
}
